const DEG = Math.PI / 180
const K = 0.01720209895 // Gaussian gravitational constant (AU^1.5/day)
const MU = K * K // AU^3 / day^2

export const AU_KM = 149_597_870.7

type Elements = {
  a: number
  e: number
  i: number
  omega: number
  w: number
  M: number
  epoch_jd: number
  q?: number | null
}

function normAnglePI(x: number) {
  return ((x + Math.PI) % (2 * Math.PI)) - Math.PI
}

function keplerEllipticE(M: number, e: number, tol = 1e-9) {
  M = normAnglePI(M)
  let E = e < 0.8 ? M : Math.PI
  for (let k = 0; k < 60; k++) {
    const f = E - e * Math.sin(E) - M
    const fp = 1 - e * Math.cos(E)
    const d = f / fp
    E -= d
    if (Math.abs(d) < tol) break
  }
  return E
}

function keplerHyperbolicF(M: number, e: number, tol = 1e-9) {
  let F = Math.asinh(M / e)
  for (let k = 0; k < 60; k++) {
    const s = Math.sinh(F)
    const c = Math.cosh(F)
    const f = e * s - F - M
    const fp = e * c - 1
    const d = f / fp
    F -= d
    if (Math.abs(d) < tol) break
  }
  return F
}

function parabolicPerifocal(q: number, dt: number) {
  const s = Math.sqrt((2 * q * q * q) / MU)
  const f = (D: number) => s * (D + (D * D * D) / 3) - dt
  const fp = (D: number) => s * (1 + D * D)
  let D = Math.sign(dt) * Math.cbrt(1e-6 + Math.abs(dt) / s)
  for (let k = 0; k < 60; k++) {
    const d = f(D) / fp(D)
    D -= d
    if (Math.abs(d) < 1e-9) break
  }
  const r = q * (1 + D * D)
  const xp = q * (1 - D * D)
  const yp = 2 * q * D
  return { xp, yp, r }
}

export function heliocentricPositionAU(el: Elements, jd: number) {
  const { a, e, i, omega, w, M, epoch_jd } = el
  const dt = jd - epoch_jd

  const cosO = Math.cos(omega * DEG)
  const sinO = Math.sin(omega * DEG)
  const cosi = Math.cos(i * DEG)
  const sini = Math.sin(i * DEG)
  const cosw = Math.cos(w * DEG)
  const sinw = Math.sin(w * DEG)

  let xp = 0
  let yp = 0

  if (e < 1 - 1e-6) {
    const n = K / Math.sqrt(a * a * a)
    const Mnow = M * DEG + n * dt
    const E = keplerEllipticE(Mnow, e)
    xp = a * (Math.cos(E) - e)
    yp = a * Math.sqrt(1 - e * e) * Math.sin(E)
  } else if (e > 1 + 1e-6) {
    const ah = Math.abs(a)
    const n = K / Math.sqrt(ah * ah * ah)
    const Mnow = M * DEG + n * dt
    const F = keplerHyperbolicF(Mnow, e)
    xp = -ah * (e - Math.cosh(F))
    yp = ah * Math.sqrt(e * e - 1) * Math.sinh(F)
  } else {
    const qEstimate = typeof a === 'number' ? a * (1 - e) : undefined
    const q = el.q ?? qEstimate ?? 1.0
    const pf = parabolicPerifocal(q, dt)
    xp = pf.xp
    yp = pf.yp
  }

  const x1 = cosw * xp - sinw * yp
  const y1 = sinw * xp + cosw * yp
  const x = cosO * x1 - sinO * y1 * cosi
  const y = sinO * x1 + cosO * y1 * cosi
  const z = y1 * sini
  return { x, y, z }
}

export function dateToJD(ms: number) {
  return ms / 86400000 + 2440587.5
}

export const auToKm = (au: number) => au * AU_KM

const J2000 = 2451545.0

const PLANETS: Record<string, Elements> = {
  Mercury: { a: 0.3871, e: 0.2056, i: 7.005, omega: 48.331, w: 29.124, M: 174.796, epoch_jd: J2000 },
  Venus: { a: 0.7233, e: 0.0068, i: 3.395, omega: 76.68, w: 54.884, M: 50.115, epoch_jd: J2000 },
  Earth: { a: 1.0, e: 0.0167, i: 0.0, omega: -11.26, w: 114.207, M: 357.517, epoch_jd: J2000 },
  Mars: { a: 1.5237, e: 0.0934, i: 1.85, omega: 49.558, w: 286.502, M: 19.373, epoch_jd: J2000 },
  Jupiter: { a: 5.2026, e: 0.0489, i: 1.303, omega: 100.464, w: 273.867, M: 20.02, epoch_jd: J2000 },
  Saturn: { a: 9.5549, e: 0.0565, i: 2.489, omega: 113.665, w: 339.392, M: 317.02, epoch_jd: J2000 },
  Uranus: { a: 19.218, e: 0.0463, i: 0.773, omega: 74.006, w: 96.998, M: 142.238, epoch_jd: J2000 },
  Neptune: { a: 30.11, e: 0.0103, i: 1.77, omega: 131.784, w: 273.187, M: 256.228, epoch_jd: J2000 },
  Pluto: { a: 39.482, e: 0.2488, i: 17.16, omega: 110.299, w: 113.834, M: 14.53, epoch_jd: J2000 },
}

export const PLANET_LIST = Object.keys(PLANETS)

export function planetPositionAU(name: string, jd: number) {
  const el = PLANETS[name]
  if (!el) return { x: 0, y: 0, z: 0 }
  return heliocentricPositionAU(el, jd)
}
